import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import LevelSelect from './LevelSelect';
import StreakPill from './StreakPill';
import AnnouncementBell from './AnnouncementBell';
import { playMark } from '../brand/celebrate';

/* ══════════════════════════════════════════════════════════════
   APP TOPBAR

   Lives in the shell, above the routed page. It used to be rendered
   *inside* the profile page and escaped that page's padding with
   `margin: -32px -32px`, which meant its width was always the
   container's width plus 64px — overflowing the main column at every
   sidebar state, and never actually spanning it. As a sibling of the
   page it simply fills the column, no negative margins involved.

   The level you are studying is a whole-app preference rather than a
   property of one screen, so it is owned here and published through
   AppLevelContext for pages that need it.
══════════════════════════════════════════════════════════════ */

const AppLevelContext = createContext({ level: 'N5', setLevel: () => {} });
export const useAppLevel = () => useContext(AppLevelContext);

const LEVEL_KEY = 'kitsu-app-level';

/* Title per route. Longest prefixes first so /practice-tests/history
   doesn't match /practice-tests. */
const TITLES = [
  ['/dashboard',            'Learn',                'Work down the path — each step unlocks the next.'],
  ['/kana',                 'Hiragana & Katakana',  'The two alphabets everything else is built on.'],
  ['/kanji',                'Kanji',                'The characters behind the words you know.'],
  ['/flashcards',           'Flashcards',           'Spaced repetition over your whole word bank.'],
  ['/quiz',                 'JLPT Quiz',            'Mixed questions at your level.'],
  ['/practice-tests/history', 'Test History',       'Every mock exam you have finished.'],
  ['/practice-tests',       'Practice Tests',       'Full mock exams under real timing.'],
  ['/grammar',              'Grammar',              'Chapters in order — each builds on the last.'],
  ['/profile',              'Profile',              null], // greeting is supplied below
  ['/settings',             'Account',              'Your sign-in details and your data.'],
];

function titleFor(pathname) {
  const hit = TITLES.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  return hit ? { title: hit[1], sub: hit[2] } : { title: 'KitsuSpeak', sub: null };
}

export function AppLevelProvider({ user, children }) {
  const [level, setLevel] = useState(() => {
    try { return localStorage.getItem(LEVEL_KEY) || user?.currentLevel || 'N5'; }
    catch (_) { return user?.currentLevel || 'N5'; }
  });

  useEffect(() => {
    try { localStorage.setItem(LEVEL_KEY, level); } catch (_) {}
  }, [level]);

  const value = useMemo(() => ({ level, setLevel }), [level]);
  return <AppLevelContext.Provider value={value}>{children}</AppLevelContext.Provider>;
}

export default function AppTopbar({ user, onOpenStreak }) {
  const { pathname } = useLocation();
  const { level, setLevel } = useAppLevel();
  const { title, sub } = titleFor(pathname);

  const hour = new Date().getHours();
  const greeting = hour < 5 ? 'Good night'
    : hour < 12 ? 'Good morning'
      : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.name?.split(' ')[0] || 'Learner';

  const subtitle = pathname.startsWith('/profile') ? `${greeting}, ${firstName}` : sub;

  return (
    <div className="app-topbar">
      <div className="app-topbar-title">
        <span>{title}</span>
        {subtitle && <small>{subtitle}</small>}
      </div>

      <div className="app-topbar-right">
        <span data-tour="level-select">
        <LevelSelect
          compact
          value={level}
          onChange={next => {
            setLevel(next);
            /* A small shake-up: the whole app's content just changed
               underneath them. */
            if (next !== level) playMark('wobble');
          }}
        />
        </span>
        <AnnouncementBell />
        <span data-tour="streak">
        <StreakPill
          streak={user?.streak ?? 0}
          lastStudied={user?.lastStudied}
          onClick={onOpenStreak}
        />
        </span>
      </div>
    </div>
  );
}
